/** Sprint 5-c · 公司會議室 · 會議記錄初稿（查看 / owner 編輯 · 列印）。 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Divider, IconButton,
  List, ListItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PrintIcon from '@mui/icons-material/Print';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { useMessage } from '../../../contexts/MessageContext';
import {
  ActionItem, getMeeting, getMinutes, Minutes, updateMinutes,
} from './services/officeMeetingService';

const OfficeMeetingMinutesPage: React.FC = () => {
  const { id } = useParams();
  const mid = Number(id);
  const nav = useNavigate();
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`officeMeeting.${k}`, d);
  const { showApiError, showSuccess } = useMessage();

  const [mn, setMn] = useState<Minutes | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [roleNames, setRoleNames] = useState<{ chair?: string | null; recorder?: string | null }>({});
  const [edit, setEdit] = useState(false);
  const [summary, setSummary] = useState('');
  const [decisions, setDecisions] = useState<{ content: string }[]>([]);
  const [items, setItems] = useState<ActionItem[]>([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getMinutes(mid);
      setMn(data); setSummary(data.summary || '');
      setDecisions(data.decisions || []); setItems(data.action_items || []);
      const meeting = await getMeeting(mid);
      // 編輯權限由後端依會議身分決定（主席 / 紀錄人員 / 建立者）
      setCanEdit(!!meeting.can_edit_minutes);
      setRoleNames({ chair: meeting.chair_name, recorder: meeting.recorder_name });
    } catch (err) { showApiError(err); }
  }, [mid, showApiError]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateMinutes(mid, { summary, decisions, action_items: items });
      setMn(updated); setEdit(false);
      showSuccess('SYS020001', { name: tt('minutes', '會議記錄') });
    } catch (err) { showApiError(err); } finally { setSaving(false); }
  };

  if (!mn) return <Box sx={{ p: 3 }}><Typography color="text.secondary">{tt('loading', '載入中…')}</Typography></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 820, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" className="no-print">
        <Button size="small" onClick={() => nav(`/ee_office_meeting/${mid}`)}>← {tt('back', '返回明細')}</Button>
        <Stack direction="row" spacing={1}>
          {canEdit && !edit && <Button size="small" onClick={() => setEdit(true)}>{tt('editBtn', '編輯')}</Button>}
          {edit && <Button size="small" variant="contained" onClick={save} disabled={saving}>{tt('saveBtn', '儲存')}</Button>}
          <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>{tt('print', '列印')}</Button>
        </Stack>
      </Stack>
      <Typography variant="h5" sx={{ my: 1 }}>{tt('minutesTitle', '會議記錄')}</Typography>
      {(roleNames.chair || roleNames.recorder) && (
        <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
          {roleNames.chair && <Chip size="small" label={`${tt('chair', '主席')}：${roleNames.chair}`} />}
          {roleNames.recorder && <Chip size="small" variant="outlined" label={`${tt('recorder', '紀錄人員')}：${roleNames.recorder}`} />}
        </Stack>
      )}

      {/* 摘要 */}
      <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
        <Typography variant="subtitle2" gutterBottom>{tt('summary', '摘要')}</Typography>
        {edit
          ? <TextField fullWidth multiline minRows={3} value={summary} onChange={(e) => setSummary(e.target.value)} />
          : <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{mn.summary || tt('none', '（無）')}</Typography>}
      </Paper>

      {/* 決議 */}
      <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
        <Typography variant="subtitle2" gutterBottom>{tt('decisions', '決議')}</Typography>
        {edit ? (
          <Stack spacing={1}>
            {decisions.map((d, i) => (
              <Stack key={i} direction="row" spacing={1}>
                <TextField fullWidth size="small" value={d.content}
                  onChange={(e) => setDecisions(decisions.map((x, j) => j === i ? { content: e.target.value } : x))} />
                <IconButton size="small" onClick={() => setDecisions(decisions.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>
              </Stack>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={() => setDecisions([...decisions, { content: '' }])}>{tt('addDecision', '新增決議')}</Button>
          </Stack>
        ) : (mn.decisions.length === 0
          ? <Typography variant="body2" color="text.secondary">{tt('none', '（無）')}</Typography>
          : <List dense disablePadding>{mn.decisions.map((d, i) => <ListItem key={i} disableGutters>• {d.content}</ListItem>)}</List>)}
      </Paper>

      {/* 待辦事項 */}
      <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
        <Typography variant="subtitle2" gutterBottom>{tt('actions', '待辦事項')}</Typography>
        {edit ? (
          <Stack spacing={1}>
            {items.map((a, i) => (
              <Stack key={a.id || i} direction="row" spacing={1} alignItems="center">
                <TextField size="small" sx={{ flex: 1 }} label={tt('aiContent', '內容')} value={a.content}
                  onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, content: e.target.value } : x))} />
                <TextField size="small" sx={{ width: 120 }} label={tt('aiAssignee', '負責人')} value={a.assignee_name || ''}
                  onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, assignee_name: e.target.value } : x))} />
                <TextField size="small" sx={{ width: 130 }} label={tt('aiDue', '期限')} value={a.due || ''}
                  onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, due: e.target.value } : x))} />
                <IconButton size="small" onClick={() => setItems(items.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>
              </Stack>
            ))}
            <Button size="small" startIcon={<AddIcon />}
              onClick={() => setItems([...items, { id: `new-${items.length}`, content: '', assignee_name: '', due: '', status: 'pending' }])}>
              {tt('addAction', '新增待辦')}</Button>
          </Stack>
        ) : (mn.action_items.length === 0
          ? <Typography variant="body2" color="text.secondary">{tt('none', '（無）')}</Typography>
          : <List dense disablePadding>{mn.action_items.map((a) => (
            <ListItem key={a.id} disableGutters sx={{ display: 'block' }}>
              <Typography variant="body2">• {a.content}</Typography>
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
                {a.assignee_name && <Chip size="small" color={a.assignee_user_id ? 'info' : 'default'} label={a.assignee_name} />}
                {a.due && <Chip size="small" variant="outlined" label={a.due} />}
              </Stack>
            </ListItem>))}</List>)}
      </Paper>

      {/* 實際出席 */}
      {mn.attendees_actual.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
          <Typography variant="subtitle2" gutterBottom>{tt('actualAttendees', '實際出席')}</Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {mn.attendees_actual.map((a) => <Chip key={a.user_id} size="small" label={a.name} />)}
          </Stack>
        </Paper>
      )}

      {/* 逐字稿 */}
      <Accordion variant="outlined" className="no-print" disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2">{tt('transcript', '逐字稿')}</Typography></AccordionSummary>
        <AccordionDetails><Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{mn.full_transcript || tt('none', '（無）')}</Typography></AccordionDetails>
      </Accordion>

      <Divider sx={{ my: 1 }} />
      <Typography variant="caption" color="text.secondary">
        {tt('cost', '本次成本')}：{mn.total_input_tokens + mn.total_output_tokens} tokens · NT${mn.total_cost_ntd.toFixed(2)}</Typography>
      <style>{'@media print{.no-print{display:none!important}}'}</style>
    </Box>
  );
};

export default OfficeMeetingMinutesPage;
